import { XhrService } from "../ts/xhr.service";
import { appendScriptToDOM } from "./utilities/functions";
import { showModal, generateNewModal, closeModal } from "./modal";
import { getNewFlashmessage, initFlashmessages } from "./flashmessages";
import EventHandler from "./dom/event-handler";
import { addErrorClass, addLoadingClass, addSuccessClass } from "./utilities/updateClasses";

export function prepareFormData(event, formEl) {
    const formData = new FormData(formEl);
    if (event.submitter?.getAttribute("name")) {
        formData.append(event.submitter.getAttribute("name"), event.submitter.getAttribute("value"));
    }
    return formData;
}

function updateDOM(response) {
    if (response.htmlToDom) {
        response.htmlToDom.forEach(({ domTargetSelector, html }) => {
            document.querySelectorAll(domTargetSelector).forEach(target => {
                target.innerHTML = html;
            });
        });
    }
}

function handleModals(response) {
    if (response.modalData?.html) {
        const MODAL_ID = 'ajax-response-modal';
        let modal = document.getElementById(MODAL_ID);

        if (!modal) {
            modal = generateNewModal("modalTemplate");
            if (modal) {
                modal.id = MODAL_ID;
            }
        }

        if (modal) {
            modal.className = 'modal';
            if (Array.isArray(response.modalData.modalClasses)) {
                response.modalData.modalClasses.forEach(cls => modal.classList.add(cls));
            }

            const modalBody = modal.querySelector(".modal__body");
            const modalTitle = modal.querySelector(".modal__title");
            if (modalTitle) modalTitle.innerHTML = response.modalData.title || "";
            if (modalBody) modalBody.innerHTML = `<div>${response.modalData.html}</div>`;

            showModal(modal);
        }
    }
}

function displayFlashMessages(response) {
    if (response.flashMessages) {
        const messageTypeMap = { info: 0, notice: 0, warning: 2, error: 3, success: 1 };
        Object.entries(response.flashMessages).forEach(([type, messages]) => {
            const typeInt = messageTypeMap[type] ?? 0;
            messages.forEach(msg => getNewFlashmessage(msg, typeInt));
        });
    }
    initFlashmessages();
}

function sendFormApi(event) {
    event.preventDefault();
    const formEl = event.target;
    const submitButton = formEl.querySelector('button[type="submit"]');
    const ajaxFn = formEl.getAttribute("data-ajax-function");
    const formData = prepareFormData(event, formEl);

    addLoadingClass(submitButton);
    EventHandler.trigger(formEl, "beforeSend.ajaxFormApi");
    sendForm(ajaxFn, formData, formEl, submitButton);
}

export function sendAjaxCustomForm(ajaxFn, formData, formEl, submitButton) {
    sendForm(ajaxFn, formData, formEl, submitButton);
}

async function sendForm(ajaxFn, formData, formEl, submitButton) {

    try {
        const response = await XhrService.generalAjaxCall(ajaxFn, formData, "POST", "json");
        if (!response?.status) throw new Error("Invalid response from server");

        const frontendActions = Array.isArray(response.frontendActions) ? response.frontendActions : [];

        updateDOM(response);

        if (response.status === "success" || response.status === "ok") {
            if (submitButton) {
                addSuccessClass(submitButton);
            }

            window.setTimeout(() => {
                if (submitButton) submitButton.classList.remove('is-success');

                if (frontendActions.length > 0) {
                    for (const action of frontendActions) {
                        switch (action) {
                            case 'close-modal':
                                try { closeModal(); } catch (e) { /* noop */ }
                                break;
                            case 'page-reload':
                                return window.location.reload();
                            default:
                                break;
                        }
                    }
                }
            }, 2000);
        } else if (response.status === "error") {
            EventHandler.trigger(formEl, "hasError.ajaxFormApi");
            if (submitButton) addErrorClass(submitButton);
        } else {
            if (submitButton) submitButton.classList.remove('is-loading');
        }

        handleModals(response);
        response.scriptToDom?.forEach(({ scriptText, addToBody }) => appendScriptToDOM(scriptText, addToBody));
        displayFlashMessages(response);

        EventHandler.trigger(formEl, "afterSuccess.ajaxFormApi");
    } catch (error) {
        console.error("AJAX Form Submission Error:", error);
        if (submitButton) submitButton.classList.remove('is-loading');
        if (submitButton) addErrorClass(submitButton);
    }
}

export function initAjaxFormApi() {
    document.addEventListener("submit", event => {
        if (event.target.classList.contains("js-ajaxFormSend")) {
            sendFormApi(event);
        }
    });
}

export function handleAjaxApiEventListeners(listenerName, beforeSend = false, afterSuccess = false, hasError = false) {
    for (const ajaxForm of document.querySelectorAll(`[data-ajax-form-listener="${listenerName}"]`)) {

        if (typeof beforeSend === "function") {
            ajaxForm.addEventListener('beforeSend.ajaxFormApi', () => {
                beforeSend();
            });
        }
        if (typeof afterSuccess === "function") {
            ajaxForm.addEventListener('afterSuccess.ajaxFormApi', () => {
                afterSuccess();
            });
        }

        if (typeof hasError === "function") {
            ajaxForm.addEventListener('hasError.ajaxFormApi', () => {
                hasError();
            });
        }

    }
}